import { QueryClient, defaultShouldDehydrateQuery, type Query } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";
import {
  api,
  type WashVisit,
  type CheckInServiceBody,
  type CheckInServiceResponse,
  type CheckInVariables,
  type AddSiteRequest,
  type UpdateSiteRequest,
  type UpsertCatalogItemRequest,
} from "./api";
import { appVersion } from "./version";
import { applyStart, applyReady, applyCollect, applyCancel, applyCapture, applyReorder } from "./optimistic";
import type { Scope } from "./auth/session";

// Query keys carry the scope (staff-auth Task 5): a shared tablet hands over from one
// signed-in user to the next, and two scopes must never collide in one cache.
export const visitsKey = (scope: Scope) => ["visits", scope.businessId, scope.siteId] as const;

// Placeholder staff id: the command payloads' StaffId fields are Guids, and a Firebase uid
// is not one. Attributing commands to the signed-in user is its own slice.
export const STAFF_ID = "00000000-0000-0000-0000-000000000000";

export interface StartPayload { serviceId: string; staffId: string; clientCommandId: string; occurredAt: string }
export interface ReadyPayload { serviceId: string; staffId: string; clientCommandId: string; occurredAt: string }
export interface CollectPayload { serviceId: string; clientCommandId: string; occurredAt: string }
export interface CancelPayload { serviceId: string; clientCommandId: string; occurredAt: string }
export interface ReorderMove { serviceId: string; newPosition: number; clientCommandId: string; occurredAt: string }
export interface ReorderPayload { moves: ReorderMove[] }

// The /settings commands. Same reason as the visit commands above: a mutationFn declared inline in
// a component cannot be dehydrated (functions don't survive JSON), so a settings save PAUSED while
// offline would be written to IndexedDB and then, on the next app start, resume with no
// mutationFn — failing immediately and permanently with no UI mounted to report it. Registering
// them here by key is what makes them genuinely resumable, not merely retryable in-session.
export interface CatalogUpsertPayload { serviceTypeId: string; body: UpsertCatalogItemRequest }
export interface SiteAddPayload { body: AddSiteRequest }
export interface SiteUpdatePayload { siteId: string; body: UpdateSiteRequest }

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      // networkMode defaults to "online": mutations pause (not fail) while offline.
      queries: { refetchInterval: 30000, retry: false },
      mutations: { retry: 3, retryDelay: (n) => Math.min(1000 * 2 ** n, 30000) },
    },
  });
}

export function registerMutationDefaults(qc: QueryClient, scope: Scope): void {
  const key = visitsKey(scope);
  type Ctx = { prev?: WashVisit[] };
  const optimistic =
    <V>(apply: (list: WashVisit[], v: V) => WashVisit[]) =>
    async (variables: V): Promise<Ctx> => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<WashVisit[]>(key);
      qc.setQueryData<WashVisit[]>(key, (old = []) => apply(old, variables));
      return { prev };
    };
  // Stage advances (start/ready/collect) wait for the API. Applying them in onMutate
  // moved the card into the next column while the request was still open, so the
  // in-flight spinner sat on the *next* action. Capture/cancel/reorder stay optimistic
  // — those are add/remove/shuffle, not a column change mid-request.
  const confirm =
    <V>(apply: (list: WashVisit[], v: V) => WashVisit[]) =>
    (_data: unknown, variables: V) => {
      qc.setQueryData<WashVisit[]>(key, (old = []) => apply(old, variables));
    };
  const cancelInFlight = async (): Promise<Ctx> => {
    await qc.cancelQueries({ queryKey: key });
    return {};
  };
  const onError = (_e: unknown, _v: unknown, ctx: Ctx | undefined) => {
    if (ctx?.prev) qc.setQueryData(key, ctx.prev);
  };
  const onSettled = () => {
    void qc.invalidateQueries({ queryKey: key });
  };

  qc.setMutationDefaults(["visit", "start"], {
    mutationFn: (p: StartPayload) => api.startService(p.serviceId, {
      staffId: p.staffId, clientCommandId: p.clientCommandId, occurredAt: p.occurredAt,
    }),
    onMutate: cancelInFlight, onSuccess: confirm(applyStart), onError, onSettled,
  });
  qc.setMutationDefaults(["visit", "ready"], {
    mutationFn: (p: ReadyPayload) => api.markServiceReady(p.serviceId, {
      staffId: p.staffId, clientCommandId: p.clientCommandId, occurredAt: p.occurredAt,
    }),
    onMutate: cancelInFlight, onSuccess: confirm(applyReady), onError, onSettled,
  });
  qc.setMutationDefaults(["visit", "collect"], {
    mutationFn: (p: CollectPayload) => api.collectService(p.serviceId, {
      clientCommandId: p.clientCommandId, occurredAt: p.occurredAt,
    }),
    onMutate: cancelInFlight, onSuccess: confirm(applyCollect), onError, onSettled,
  });
  qc.setMutationDefaults(["visit", "cancel"], {
    mutationFn: (p: CancelPayload) => api.cancelService(p.serviceId, {
      clientCommandId: p.clientCommandId, occurredAt: p.occurredAt,
    }),
    onMutate: optimistic(applyCancel), onError, onSettled,
  });
  qc.setMutationDefaults(["visit", "reorder"], {
    mutationFn: (p: ReorderPayload) => Promise.all(p.moves.map((move) => api.reorderService(move.serviceId, {
      newPosition: move.newPosition, clientCommandId: move.clientCommandId, occurredAt: move.occurredAt,
    }))),
    onMutate: optimistic(applyReorder), onError, onSettled,
  });
  qc.setMutationDefaults(["visit", "capture"], {
    // serviceName labels the optimistic card. It is not a check-in field.
    mutationFn: (p: CheckInVariables) => {
      const body: CheckInServiceBody & { serviceName?: string } = { ...p };
      delete body.serviceName;
      return api.checkInService(body);
    },
    onMutate: optimistic(applyCapture),
    // onSettled refetches the open-services query (visitsKey). onSuccess still rewrites
    // this card's clientCommandId to the stream id before that refetch lands.
    onSuccess: (data: CheckInServiceResponse, variables: CheckInVariables) => {
      qc.setQueryData<WashVisit[]>(key, (old = []) =>
        old.map((v) => (v.id === variables.clientCommandId ? { ...v, id: data.serviceId } : v)),
      );
    },
    onError,
    onSettled,
  });

  // Settings commands. No optimistic onMutate: the settings screen shows server state and has no
  // offline-first board to keep consistent, so a plain refetch on settle is the whole story — and
  // it has to live HERE rather than in the component, because a mutation resumed after a reload
  // has no component mounted to invalidate anything.
  //
  // The catalog upsert invalidates BOTH keys: ["catalog-items"] is the settings list, and
  // ["catalog", businessId, siteId] (visitsApi.useCatalog) is the board's effective catalog that
  // WalkInModal prices from. Without the second one, the board only happens to be correct because
  // navigating unmounts it and staleTime: 0 forces a refetch.
  qc.setMutationDefaults(["settings", "catalog-upsert"], {
    mutationFn: (p: CatalogUpsertPayload) => api.upsertCatalogItem(scope.businessId, p.serviceTypeId, p.body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["catalog-items", scope.businessId] });
      void qc.invalidateQueries({ queryKey: ["catalog", scope.businessId] });
    },
  });
  qc.setMutationDefaults(["settings", "site-add"], {
    mutationFn: (p: SiteAddPayload) => api.addSite(scope.businessId, p.body),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["sites", scope.businessId] }); },
  });
  qc.setMutationDefaults(["settings", "site-update"], {
    mutationFn: (p: SiteUpdatePayload) => api.updateSite(scope.businessId, p.siteId, p.body),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["sites", scope.businessId] }); },
  });
}

const idbStorage = {
  getItem: (k: string) => get<string>(k).then((v) => v ?? null),
  setItem: (k: string, v: string) => set(k, v),
  removeItem: (k: string) => del(k),
};

// One persisted store per (uid, business, site). Two hazards ride on this key:
//   1. the OUTBOX — a command queued by one user must never resume under another's identity
//      after a sign-out/sign-in on a shared tablet;
//   2. the QUERY CACHE — rehydrating the previous session's board would show one business's
//      queue (phone numbers, registrations) to the next person to sign in.
// Keying the storage itself is what makes both structural rather than a clearing routine
// somebody has to remember to call. Sign-out clears the live cache as well — see
// auth/performSignOut.ts, which clears the query cache but deliberately NOT the mutation
// cache (those are unsent offline commands, ADR-0013).
export function persistKey(uid: string, scope: Scope): string {
  return `carwash-staff-queryclient:${uid}:${scope.businessId}:${scope.siteId}`;
}

// The invites cache holds LIVE CREDENTIALS: every pending invite's inviteUrl carries the
// plaintext token that lets whoever holds it join the business. The rest of this slice keeps
// that token out of request URLs, access logs and Sentry, so it must not then be written
// unencrypted to a shared tablet's IndexedDB, where the 24h maxAge would leave it readable
// by the next person to pick the device up (or by any same-origin XSS) long after the owner
// closed the app. It is cheap to refetch and useless offline — nothing is lost by dropping it.
const PERSIST_NEVER = ["invites"];

export function createPersistOptions(uid: string, scope: Scope) {
  return {
    persister: createAsyncStoragePersister({ storage: idbStorage, key: persistKey(uid, scope) }),
    maxAge: 1000 * 60 * 60 * 24, // 24h
    buster: appVersion.version, // drop the cache when the app version changes
    // Persist the outbox: paused/pending mutations are dehydrated alongside queries.
    dehydrateOptions: {
      shouldDehydrateMutation: () => true,
      // Everything else keeps the library's default rule (successful queries only) — this
      // only subtracts. Sign-out is unaffected: performSignOut clears the cache and the
      // emptied state is written back, which stays the guarantee for every other query.
      shouldDehydrateQuery: (query: Query) =>
        !PERSIST_NEVER.includes(query.queryKey[0] as string) && defaultShouldDehydrateQuery(query),
    },
  };
}
